/**
 * Utility functions for formatting data in IRIP documents
 * These functions handle common formatting tasks across multiple IRIP sections
 */

/**
 * Formats a grade for display in IRIP documents
 * Converts grade 0 to "K" and other grades to "Grade X" format
 * @param {string|number} grade - The grade level
 * @returns {string} Formatted grade display
 */
export const formatGradeDisplay = (grade) => {
  if (grade === '0' || grade === 0) {
    return 'K';
  }
  return `Grade ${grade}`;
};

/**
 * Converts milliseconds to minutes with proper rounding
 * @param {number} milliseconds - Time in milliseconds
 * @returns {number} Time in minutes, rounded to nearest whole number
 */
export const millisecondsToMinutes = (milliseconds) => {
  return Math.round((milliseconds || 0) / 60000);
};

/**
 * Gets color for usage time metrics based on 30-minute threshold
 * @param {number} minutes - Time in minutes
 * @returns {string} Hex color code: green for >=30min, red for <30min
 */
export const getUsageTimeColor = (minutes) => {
  return minutes >= 30 ? "66CC99" : "FF7A7A"; // Green if >=30min, red if <30min
};

/**
 * Formats a percentage with specified decimal places
 * @param {number} value - The decimal value (0-1)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === undefined || value === null) {
    return "No Data";
  }
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Formats time duration for display
 * @param {number} minutes - Time in minutes
 * @param {string} unit - Unit to display (default: "Minutes")
 * @returns {string} Formatted time string
 */
export const formatTimeDisplay = (minutes, unit = "Minutes") => {
  return `${minutes} ${unit}`;
};

/**
 * Formats story count for display
 * @param {number} count - Number of stories
 * @returns {string} Formatted story count string
 */
export const formatStoryCount = (count) => {
  return `${count} ${count === 1 ? 'Story' : 'Stories'}`;
};

/**
 * Safely gets a nested property from an object with fallback
 * @param {Object} obj - The object to get property from
 * @param {string} path - Dot-separated path to property
 * @param {*} fallback - Fallback value if property doesn't exist
 * @returns {*} The property value or fallback
 */
export const safeGet = (obj, path, fallback = null) => {
  try {
    return path.split('.').reduce((current, key) => current?.[key], obj) ?? fallback;
  } catch (error) {
    return fallback;
  }
};

/**
 * Normalizes curriculum name for icon lookup
 * Converts to lowercase, replaces spaces with underscores, removes special chars
 * @param {string} curriculumName - The curriculum name
 * @returns {string} Normalized curriculum name
 */
export const normalizeCurriculumName = (curriculumName) => {
  if (!curriculumName) return "";
  
  return curriculumName
    .toLowerCase()
    .replace(/\s+/g, "_")  // Replace spaces with underscores
    .replace(/[^a-z0-9_]/g, ""); // Remove special characters
};

/**
 * Creates a safe display name from various name fields
 * @param {Object} item - Object that might have displayName, name, skillName, etc.
 * @param {string} fallback - Fallback text if no name found
 * @returns {string} The best available display name
 */
export const getDisplayName = (item, fallback = "Unknown") => {
  if (!item) return fallback;
  
  return item.displayName || 
         item.name || 
         item.skillName || 
         item.lessonName || 
         item.unitName ||
         item.id || 
         fallback;
}; 